/**
 * SC-760 / SC-775 — le chrome « étape par étape » de la page de validation.
 *
 * SC-760 avait gardé toutes les étapes visibles d'un seul défilé : un assistant
 * qui n'affiche qu'une étape peut cacher le champ obligatoire que le client ne
 * trouve pas, et c'est ce qui laissait « Passer la commande » gris sans
 * explication. SC-775 passe bien à un assistant — la page défilait sur plusieurs
 * écrans — mais reprend cette garantie autrement :
 *
 *  - le fil des étapes reste complet en haut, il porte l'état de CHAQUE étape et
 *    mène à n'importe laquelle en un clic (y compris en avant) : rien n'est
 *    enfermé derrière un « Suivant » ;
 *  - « Suivant » refuse d'avancer sur une étape incomplète, met le champ fautif
 *    au rouge et lui donne le focus, au lieu de laisser le client découvrir le
 *    problème trois écrans plus loin devant un bouton gris.
 */
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

/** `blocked` = cette étape est ce qui empêche d'envoyer la commande. */
export type StepState = 'done' | 'todo' | 'blocked';

export interface CheckoutStep {
  /** Ancre HTML de la section : c'est la cible du raccourci « Y aller ». */
  id: string;
  label: string;
  state: StepState;
  /** Étape que le client peut sauter (note, fidélité). */
  optional?: boolean;
}

/**
 * L'en-tête collant du site recouvre le haut de la page : les sections portent
 * un `scroll-margin-top` de sa hauteur mesurée (`--site-header-h`, posée par
 * SiteHeader) pour qu'une étape visée ne se retrouve pas dessous.
 */
export const STEP_SCROLL_MARGIN = 'calc(var(--site-header-h, 110px) + 12px)';

export function scrollToStep(id: string) {
  const el = typeof document === 'undefined' ? null : document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function badgeClass(state: StepState) {
  if (state === 'done') return 'bg-mint text-ink';
  if (state === 'blocked') return 'text-white';
  return 'bg-paper text-ink';
}

const badgeStyle = (state: StepState) =>
  state === 'blocked' ? { background: 'var(--sale-red)' } : undefined;

/**
 * Le fil des étapes, en haut de page. Il enveloppe au lieu de défiler
 * horizontalement : sur un téléphone, une étape poussée hors de l'écran est une
 * étape que le client ne saura pas qu'il doit remplir.
 *
 * SC-775 : c'est aussi la navigation de l'assistant — la pastille de l'étape
 * affichée est marquée `aria-current`, et toutes les autres restent cliquables,
 * en arrière comme en avant.
 */
export function StepRail({
  steps,
  onJump,
  activeId,
}: {
  steps: CheckoutStep[];
  onJump: (id: string) => void;
  /** Étape actuellement affichée. Omis là où la page n'est pas un assistant. */
  activeId?: string;
}) {
  const { t } = useTranslation();
  if (steps.length < 2) return null;

  return (
    <nav aria-label={t('checkout.steps.navLabel')} data-testid="checkout-step-rail">
      <ol className="flex flex-wrap items-center gap-1 list-none p-0 m-0">
        {steps.map((step, i) => {
          const active = step.id === activeId;
          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => onJump(step.id)}
                data-testid={`step-rail-${step.id}`}
                data-state={step.state}
                data-active={active ? 'true' : undefined}
                aria-current={active ? 'step' : undefined}
                className={`flex items-center gap-1 rounded-btn-sm border-2 px-1.5 py-1 font-mono text-[11px] leading-none transition-colors ${
                  step.state === 'blocked'
                    ? 'border-sale-red bg-cream text-ink'
                    : step.state === 'done'
                      ? 'border-ink bg-paper text-ink'
                      : 'border-ink/30 bg-paper text-ink/70'
                } ${active ? 'bg-cream font-bold shadow-stamp-sm' : ''}`}
              >
                <span
                  aria-hidden="true"
                  className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border-2 border-ink text-[10px] font-bold ${badgeClass(
                    step.state,
                  )}`}
                  style={badgeStyle(step.state)}
                >
                  {step.state === 'done' ? '✓' : step.state === 'blocked' ? '!' : i + 1}
                </span>
                {step.label}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Une section de la page, avec son numéro et son état. Le cadre passe au rouge
 * dès que l'étape bloque l'envoi — pas seulement après un clic sur le bouton :
 * le bouton, lui, est déjà gris à ce moment-là.
 */
export function StepCard({
  step,
  index,
  children,
  sectionRef,
  testId,
  headerAside,
  hidden = false,
}: {
  step: CheckoutStep;
  /**
   * Rang affiché (1-based) dans le fil des étapes. Omis là où la page n'est pas
   * numérotée (table partagée) : la carte garde alors son état, sans numéro qui
   * ne correspondrait à rien.
   */
  index?: number;
  children: ReactNode;
  sectionRef?: RefObject<HTMLElement>;
  testId?: string;
  /** Contenu aligné à droite du titre (le supplément des questions, p. ex.). */
  headerAside?: ReactNode;
  /**
   * SC-775 — l'assistant n'affiche qu'une étape. On pose l'attribut `hidden`
   * plutôt que de démonter la section : les champs déjà saisis, le formulaire
   * de carte et les `ref` de focus survivent au va-et-vient entre les étapes.
   */
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section
      id={step.id}
      ref={sectionRef}
      hidden={hidden || undefined}
      data-testid={testId}
      data-step-state={step.state}
      style={{ scrollMarginTop: STEP_SCROLL_MARGIN }}
      className={`border-2 bg-paper p-4 shadow-stamp ${
        step.state === 'blocked' ? 'border-sale-red' : 'border-ink'
      }`}
    >
      <div className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        {index != null && (
          <span
            aria-hidden="true"
            className={`flex h-6 w-6 flex-none items-center justify-center rounded-full border-2 border-ink font-mono text-[11px] font-bold leading-none ${badgeClass(
              step.state,
            )}`}
            style={badgeStyle(step.state)}
          >
            {index}
          </span>
        )}
        <h2 className="font-display text-lg leading-tight">{step.label}</h2>
        {step.state === 'blocked' ? (
          <span
            className="rounded-btn-sm border-2 border-ink px-2 py-[2px] font-mono text-[11px] font-bold text-white"
            style={{ background: 'var(--sale-red)' }}
          >
            {t('checkout.steps.todoPill')}
          </span>
        ) : (
          step.optional && (
            <span className="font-mono text-xs text-ink/60">({t('checkout.steps.optional')})</span>
          )
        )}
        {headerAside && <span className="ms-auto">{headerAside}</span>}
      </div>
      {children}
    </section>
  );
}
